import {
  Direction,
  Position,
  SkillData,
  SkillEvent,
  SkillEvent_AnchoredInvoke,
  SkillEvent_Cleanse,
  SkillEvent_CooldownSkip,
  SkillEvent_GapClose,
  SkillEvent_Global,
  SkillEvent_Heal,
  SkillEvent_MeleeHit,
  SkillEvent_ParticleEmission,
  SkillEvent_ProjectileCast,
  SkillEvent_Pull,
  SkillEvent_Shield,
  SkillEvent_StealBuff,
  SkillEvent_Tether,
  SkillEvent_Vfx,
  StatusEffect,
} from "types/battle-engine/battle";
import { Application, Container, Graphics } from "pixi.js";
import { PokemonSkill } from "./pokemon-skill";
import { VFX_SPRITE_FRAME_COUNT_DATA } from "@/data/vfx-sprite-frame-count-data";
import {
  MS_PER_TICK,
  gridTileSize,
  gridMarginX,
  gridMarginY,
} from "types/battle-engine/constants";
import { ClientSkillCastTile } from "./client-skill-cast-tile";
import { ClientPokemon } from "./client-pokemon";
import { VFX } from "./client-vfx";
import { Projectile } from "./client-projectile";
import { Particle } from "./client-particle";
import { vectorToDir } from "../utils/vector-to-dir";
import { Pokemon } from "./pokemon";
import { calculateDamage } from "../utils/calculate-damage";
import { getHealingMultiplier } from "game/duel-engine/utils/stat-scaling";

export class ClientPokemonSkill extends PokemonSkill {
  private parent: Container;
  private app: Application;
  protected override pokemon: ClientPokemon;

  constructor(
    data: SkillData,
    pokemon: ClientPokemon,
    app: Application,
    parent?: Container,
  ) {
    super(data, pokemon);

    this.app = app;
    this.parent = parent ?? app.stage;
    this.pokemon = pokemon;
  }

  public override async useSkill(
    skillData: SkillData,
    _currentTick?: number,
    targetPokemon?: Pokemon | null,
  ) {
    super.useSkill(skillData);

    const currentTick = _currentTick ?? this.currentTick;

    // If skill should align to user dir, rotate each point to match the user's direction
    if (skillData.align_to_user_dir && skillData.cast_pattern) {
      const direction = this.pokemon.direction as Direction;
      const newPoints = skillData.cast_pattern[direction];

      const anchoredInvokeEvents = skillData.on_cast.filter((event) => {
        return event.__typename === "AnchoredInvoke";
      }) as SkillEvent_AnchoredInvoke[];

      anchoredInvokeEvents.forEach((event, index) => {
        const newPointData = newPoints[index];

        if (!newPointData) {
          event.relative_x = 0;
          event.relative_y = 0;
          event.blocks_event_pipeline = false;
          return;
        }

        event.relative_x = newPointData[0];
        event.relative_y = newPointData[1];
        event.blocks_event_pipeline = newPointData[2] ?? false;
      });
    }

    // Check for charging
    if (skillData.charging) {
      const chargingTicks = skillData.charging.duration_ticks;

      const delayMs = chargingTicks * MS_PER_TICK;

      this.pokemon.isCharging = true;
      this.pokemon.setAnimation(["Rumble", "RearUp", "Charge"], Direction.S);

      // Apply channel_status to target during channeling (for visual effects like KNOCKUP)
      if (
        skillData.charging.is_channel &&
        skillData.charging.channel_status &&
        targetPokemon
      ) {
        const clientTarget = targetPokemon as ClientPokemon;
        clientTarget.applyStatusEffect(
          skillData.charging.channel_status,
          this.pokemon.id,
          currentTick,
        );
        clientTarget.updateStatusEffectIcons();
      }

      const chargingAnimOnEnd = skillData.charging.anim_on_end ?? {
        name: "Idle",
        dir: "target",
      };

      let chargingAnimOnEndName: string | string[] = "Idle";
      switch (chargingAnimOnEnd.name) {
        case "rotate":
          chargingAnimOnEndName = "Rotate";
          break;
        case "melee":
          chargingAnimOnEndName = this.pokemon.SpecialMeleeAnimation;
          break;
        case "special":
          chargingAnimOnEndName = this.pokemon.SpecialRangedAnimation;
          break;
        case "pose":
          chargingAnimOnEndName = ["Pose", "Idle"];
          break;
        case "idle":
        default:
          break;
      }

      let chargingAnimOnEndDir = Direction.S;
      if (chargingAnimOnEnd.dir === "target" && targetPokemon) {
        const dx = targetPokemon.Position.x - this.pokemon.Position.x;
        const dy = targetPokemon.Position.y - this.pokemon.Position.y;
        const directionToTarget = vectorToDir(dx, dy);
        chargingAnimOnEndDir = directionToTarget;
      }

      setTimeout(() => {
        this.pokemon.isCharging = false;
        this.pokemon.setAnimationOnce(
          chargingAnimOnEndName,
          chargingAnimOnEndDir,
        );
      }, delayMs);
    }

    for (const event of skillData.on_cast) {
      let actionPromise: any;

      switch (event.__typename) {
        case "MeleeHit":
          actionPromise = this.handleMeleeHit.bind(this);
          break;

        case "GapClose":
          actionPromise = this.handleGapClose.bind(this);
          break;

        case "ProjectileCast":
          actionPromise = this.handleProjectileCast.bind(this);

          break;
        case "Heal":
          actionPromise = this.handleHeal.bind(this);
          break;
        case "Shield":
          actionPromise = this.handleShield.bind(this);
          break;
        case "AnchoredInvoke":
          actionPromise = this.handleAnchoredInvoke.bind(this);

          break;
        case "Vfx":
          actionPromise = this.handleVfx.bind(this);
          break;
        case "ParticleEmission":
          actionPromise = this.handleParticleEmission.bind(this);
          break;
        case "Global":
          actionPromise = this.handleGlobal.bind(this);
          break;
        case "Wait":
          actionPromise = () => {};
          break;
        case "Cleanse":
          actionPromise = this.handleCleanse.bind(this);
          break;
        case "StealBuff":
          actionPromise = this.handleStealBuff.bind(this);
          break;
        case "CooldownSkip":
          actionPromise = this.handleCooldownSkip.bind(this);
          break;
        case "Tether":
          actionPromise = this.handleTether.bind(this);
          break;
        case "Pull":
          actionPromise = this.handlePull.bind(this);
          break;
        default:
          throw new Error(`Unknown event type: ${event.__typename}`);
      }

      if (event.starting_tick === currentTick) {
        void Promise.resolve(
          actionPromise?.({ event, skillData, tick: currentTick }),
        );
        continue;
      }

      const delayMs = (event.starting_tick - currentTick) * MS_PER_TICK;

      // Register delayed action to apply the action and continue the event pipeline
      setTimeout(() => {
        void Promise.resolve(
          actionPromise?.({ event, skillData, tick: currentTick }),
        );
      }, delayMs);
    }
  }

  protected override triggerOnHit(events: SkillEvent[], currentTick: number) {
    for (const event of events) {
      let actionPromise: (() => void) | void = undefined;

      switch (event.__typename) {
        case "Vfx":
          actionPromise = () =>
            this.handleVfx({
              event: event as SkillEvent_Vfx,
              skillData: this.data,
            });
          break;
        case "AnchoredInvoke":
          actionPromise = () =>
            this.handleAnchoredInvoke({
              event: event as SkillEvent_AnchoredInvoke,
              skillData: this.data,
            });
          break;
        case "Heal":
          actionPromise = () =>
            this.handleHeal({
              event: event as SkillEvent_Heal,
              skillData: this.data,
            });
          break;
        case "Shield":
          actionPromise = () =>
            this.handleShield({
              event: event as SkillEvent_Shield,
              skillData: this.data,
            });
          break;
        default:
          // Ignore unsupported on_hit event types
          break;
      }

      if (event.starting_tick === currentTick) {
        void Promise.resolve(actionPromise?.());
        continue;
      }

      const delayMs = (event.starting_tick - currentTick) * MS_PER_TICK;

      // Register delayed action to apply the action and continue the event pipeline
      setTimeout(() => {
        void Promise.resolve(actionPromise?.());
      }, delayMs);
    }

    return;
  }

  protected override async handleProjectileCast({
    event: projectileCastEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_ProjectileCast;
    skillData: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    const impactTick = projectileCastEvent.battle_log?.impact_tick;
    if (!impactTick)
      throw new Error(
        `Impact tick not found => id: ${projectileCastEvent.target.id}`,
      );

    const targetPokemon = this.pokemon.getEnemyPokemon(
      projectileCastEvent.target.id,
    ) as ClientPokemon | null;
    if (!targetPokemon)
      throw new Error(
        `${this.pokemon.id} cant find target => id: ${projectileCastEvent.target.id}`,
      );

    const travelDurationMs =
      (impactTick - projectileCastEvent.starting_tick) * MS_PER_TICK;

    const spriteFrameCount =
      VFX_SPRITE_FRAME_COUNT_DATA[projectileCastEvent.sprite_path] ?? 1;

    // Calculate directional offset for projectile spawn
    const dirX = targetPokemon.Position.x - this.pokemon.Position.x;
    const dirY = targetPokemon.Position.y - this.pokemon.Position.y;
    const distance = Math.sqrt(dirX * dirX + dirY * dirY);
    const SPAWN_OFFSET = 8; // Tiny offset in pixels
    const offsetX = (dirX / distance) * SPAWN_OFFSET;
    const offsetY = (dirY / distance) * SPAWN_OFFSET;

    // Turn to face the enemy and play ranged attack animation
    this.pokemon.turnToFaceTarget(targetPokemon);

    const directionToTarget = vectorToDir(dirX, dirY);

    const animName = this.pokemon.SpecialRangedAnimation;
    this.pokemon.setAnimationOnce([animName], directionToTarget);

    const hitDelay = this.pokemon.getAnimationHitDelay(animName);
    const delayMs =
      hitDelay ?? projectileCastEvent.duration_ticks * MS_PER_TICK * 0.275;

    await this.pokemon.sleep(delayMs);

    // Spawn projectile
    new Projectile({
      app: this.app,
      parent: this.parent!,
      spriteUrl: projectileCastEvent.sprite_path,
      spriteFrameCount: spriteFrameCount,
      spriteScale: projectileCastEvent.sprite_scale ?? 1,
      from: {
        x: this.pokemon.Position.x + offsetX,
        y: this.pokemon.Position.y + offsetY,
      },
      to: {
        x: targetPokemon.Position.x,
        y: targetPokemon.Position.y,
      },
      durationMs: travelDurationMs,
      onImpact: () => {
        if (targetPokemon && !targetPokemon.IsDestroyed) {
          const damage = calculateDamage({
            inbound: {
              damage: projectileCastEvent.damage,
              type: skillData.pokemon_type,
              attackerStatusEffects: Array.from(
                this.pokemon.ActiveStatusEffects.values(),
              ),
              attackerEffectiveAP: this.pokemon.effectiveAbilityPower,
            },
            defender: {
              pokemon: targetPokemon.PokemonData,
              defenderStatusEffects: Array.from(
                targetPokemon.ActiveStatusEffects.values(),
              ),
              defenderDodgeChance: targetPokemon.dodgeChance,
            },
          });

          targetPokemon.takeDamage(damage);

          // Apply status effects from the projectile hit
          if (
            projectileCastEvent.status_effects &&
            projectileCastEvent.status_effects.length > 0
          ) {
            for (const statusEffect of projectileCastEvent.status_effects) {
              targetPokemon.applyStatusEffect(
                statusEffect,
                this.pokemon.id,
                impactTick,
              );
            }
            // Update visual display immediately after applying status effects
            targetPokemon.updateStatusEffectIcons();
          }
        }

        // Use prepared on_hit events from server (with populated target ids)
        if (projectileCastEvent.prepared_on_hit_events) {
          this.triggerOnHit(
            projectileCastEvent.prepared_on_hit_events,
            impactTick,
          );
        }
      },
    });
  }

  protected override async handleMeleeHit({
    event: meleeHitEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_MeleeHit;
    skillData: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    const targetPokemon = this.pokemon.getEnemyPokemon(
      meleeHitEvent.target.id,
    ) as ClientPokemon | null;
    if (!targetPokemon)
      throw new Error(
        `${this.pokemon.id} cant find target => id: ${meleeHitEvent.target.id}`,
      );

    this.pokemon.turnToFaceTarget(targetPokemon);

    const directionToTarget = vectorToDir(
      targetPokemon.Position.x - this.pokemon.Position.x,
      targetPokemon.Position.y - this.pokemon.Position.y,
    );

    // Check if this is a tail-swing skill (aqua-tail, etc.)
    const isTailSwingSkill = skillData.id === "aqua-tail";

    if (isTailSwingSkill) {
      // Tail swing animation: fast swing in -> damage -> slower recovery out
      this.pokemon.setAnimation(["Idle"], directionToTarget);

      // Fast swing "in" phase (wind up and strike)
      await this.pokemon.playTailSwingAnimation("in", 360);

      // Deal damage at the peak of the swing
      const damage = calculateDamage({
        inbound: {
          damage: meleeHitEvent.damage,
          type: skillData.pokemon_type,
          attackerStatusEffects: Array.from(
            this.pokemon.ActiveStatusEffects.values(),
          ),
          attackerEffectiveAP: this.pokemon.effectiveAbilityPower,
        },
        defender: {
          pokemon: targetPokemon.PokemonData,
          defenderStatusEffects: Array.from(
            targetPokemon.ActiveStatusEffects.values(),
          ),
          defenderDodgeChance: targetPokemon.dodgeChance,
        },
      });

      targetPokemon.takeDamage(damage);

      // Apply status effects
      if (
        meleeHitEvent.status_effects &&
        meleeHitEvent.status_effects.length > 0
      ) {
        for (const statusEffect of meleeHitEvent.status_effects) {
          targetPokemon.applyStatusEffect(
            statusEffect,
            this.pokemon.id,
            meleeHitEvent.starting_tick,
          );
        }
        targetPokemon.updateStatusEffectIcons();
      }

      // Trigger on_hit events
      if (meleeHitEvent.prepared_on_hit_events) {
        this.triggerOnHit(
          meleeHitEvent.prepared_on_hit_events,
          meleeHitEvent.starting_tick + meleeHitEvent.duration_ticks,
        );
      }

      // Slower swing "out" phase (recovery)
      await this.pokemon.playTailSwingAnimation("out", 360);

      return;
    }

    // Default melee hit behavior for non-tail-swing skills
    const animName = this.pokemon.SpecialMeleeAnimation;
    this.pokemon.setAnimationOnce([animName], directionToTarget);

    const hitDelay = this.pokemon.getAnimationHitDelay(animName);
    const delayMs =
      hitDelay ?? meleeHitEvent.duration_ticks * MS_PER_TICK * 0.5;

    await this.pokemon.sleep(delayMs);

    const damage = calculateDamage({
      inbound: {
        damage: meleeHitEvent.damage,
        type: skillData.pokemon_type,
        attackerStatusEffects: Array.from(
          this.pokemon.ActiveStatusEffects.values(),
        ),
        attackerEffectiveAP: this.pokemon.effectiveAbilityPower,
      },
      defender: {
        pokemon: targetPokemon.PokemonData,
        defenderStatusEffects: Array.from(
          targetPokemon.ActiveStatusEffects.values(),
        ),
        defenderDodgeChance: targetPokemon.dodgeChance,
      },
    });

    // Show visual damage effect
    targetPokemon.takeDamage(damage);

    // Apply status effects from the melee hit
    if (
      meleeHitEvent.status_effects &&
      meleeHitEvent.status_effects.length > 0
    ) {
      for (const statusEffect of meleeHitEvent.status_effects) {
        targetPokemon.applyStatusEffect(
          statusEffect,
          this.pokemon.id,
          meleeHitEvent.starting_tick,
        );
      }
      // Update visual display immediately after applying status effects
      targetPokemon.updateStatusEffectIcons();
    }

    // Use prepared on_hit events from server (with populated target ids)
    if (meleeHitEvent.prepared_on_hit_events) {
      this.triggerOnHit(
        meleeHitEvent.prepared_on_hit_events,
        meleeHitEvent.starting_tick + meleeHitEvent.duration_ticks,
      );
    }

    await this.pokemon.sleep(meleeHitEvent.duration_ticks * MS_PER_TICK * 0.5);
  }

  protected override async handleGapClose({
    event: gapCloseEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_GapClose;
    skillData: SkillData;
    currentTick?: number;
  }) {
    const targetPokemon = this.pokemon.getEnemyPokemon(
      gapCloseEvent.target.id,
    ) as ClientPokemon | null;
    if (!targetPokemon)
      throw new Error(
        `${this.pokemon.id} cant find target => id: ${gapCloseEvent.target.id}`,
      );

    this.pokemon.turnToFaceTarget(targetPokemon);

    // Dash to target position with speed lines
    const targetPos = gapCloseEvent.battle_log?.target_position;
    if (targetPos) {
      const pixelPos = {
        x: (targetPos.x + 1) * gridTileSize + gridMarginX,
        y: (targetPos.y + 1) * gridTileSize + gridMarginY,
      };
      await this.pokemon.applyDash(pixelPos, 80);
    }

    const damage = calculateDamage({
      inbound: {
        damage: gapCloseEvent.damage,
        type: skillData.pokemon_type,
        attackerStatusEffects: Array.from(
          this.pokemon.ActiveStatusEffects.values(),
        ),
        attackerEffectiveAP: this.pokemon.effectiveAbilityPower,
      },
      defender: {
        pokemon: targetPokemon.PokemonData,
        defenderStatusEffects: Array.from(
          targetPokemon.ActiveStatusEffects.values(),
        ),
        defenderDodgeChance: targetPokemon.dodgeChance,
      },
    });

    // Show visual damage effect
    targetPokemon.takeDamage(damage);

    // Apply status effects from the melee hit
    if (
      gapCloseEvent.status_effects &&
      gapCloseEvent.status_effects.length > 0
    ) {
      for (const statusEffect of gapCloseEvent.status_effects) {
        targetPokemon.applyStatusEffect(
          statusEffect,
          this.pokemon.id,
          gapCloseEvent.starting_tick,
        );
      }
      // Update visual display immediately after applying status effects
      targetPokemon.updateStatusEffectIcons();
    }

    // Use prepared on_hit events from server (with populated target ids)
    if (gapCloseEvent.prepared_on_hit_events) {
      this.triggerOnHit(
        gapCloseEvent.prepared_on_hit_events,
        gapCloseEvent.starting_tick + gapCloseEvent.duration_ticks,
      );
    }

    // Wait remaining duration for skill timing
    await this.pokemon.sleep(gapCloseEvent.duration_ticks * MS_PER_TICK * 0.5);
  }

  protected override async handleHeal({
    event: healEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_Heal;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    this.pokemon.setAnimation(
      ["Rumble", "RearUp", "Charge"],
      this.pokemon.CurrentDir,
    );

    const healMultiplier = getHealingMultiplier(
      this.pokemon.effectiveAbilityPower,
    );
    const healAmount = Math.round(healEvent.heal_amount * healMultiplier);

    // Handle specific ally heal
    if (healEvent.target.type === "ally") {
      const targetAlly = this.pokemon.getAllyPokemon(
        healEvent.target.id,
      ) as ClientPokemon | null;
      if (!targetAlly) {
        throw new Error(`Ally not found => id: ${healEvent.target.id}`);
      }
      targetAlly.heal(healAmount);
      targetAlly.showFloatingHeal(healAmount);

      await this.pokemon.sleep(healEvent.duration_ticks * MS_PER_TICK);

      return;
    }

    // Handle self heal
    if (healEvent.target.type === "self") {
      this.pokemon.heal(healAmount);
      this.pokemon.showFloatingHeal(healAmount);

      await this.pokemon.sleep(healEvent.duration_ticks * MS_PER_TICK);

      return;
    }
  }

  protected override async handleShield({
    event: shieldEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_Shield;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    let targetPokemon: ClientPokemon;

    if (shieldEvent.target.type === "self") {
      targetPokemon = this.pokemon;
    } else if (shieldEvent.target.type === "ally") {
      const ally = this.pokemon.getAllyPokemon(shieldEvent.target.id);
      if (!ally) {
        throw new Error(`Ally not found => id: ${shieldEvent.target.id}`);
      }
      targetPokemon = ally as ClientPokemon;
    } else {
      throw new Error("Shield cannot target enemies");
    }

    this.pokemon.setAnimation(
      ["Rumble", "RearUp", "Charge"],
      this.pokemon.CurrentDir,
    );

    // Add shield (this will update maxShieldAmount internally)
    targetPokemon.addShield(
      shieldEvent.shield_amount,
      shieldEvent.shield_duration_ticks,
    );

    await this.pokemon.sleep(shieldEvent.duration_ticks * MS_PER_TICK);
  }

  protected override handleAnchoredInvoke({
    event: anchoredInvokeEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_AnchoredInvoke;
    skillData: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    let anchorX = this.pokemon.Position.x;
    let anchorY = this.pokemon.Position.y;

    // Determine anchor position based on target type
    if (anchoredInvokeEvent.target.type === "self") {
      anchorX = this.pokemon.Position.x;
      anchorY = this.pokemon.Position.y;
    } else if (anchoredInvokeEvent.target.type === "ally") {
      const ally = this.pokemon.getAllyPokemon(anchoredInvokeEvent.target.id);
      if (!ally) {
        throw new Error(
          `Ally not found => id: ${anchoredInvokeEvent.target.id}`,
        );
      }
      anchorX = ally.Position.x;
      anchorY = ally.Position.y;
    } else if (anchoredInvokeEvent.target.type === "enemy") {
      const enemy = this.pokemon.getEnemyPokemon(anchoredInvokeEvent.target.id);
      if (!enemy) {
        throw new Error(
          `Enemy not found => id: ${anchoredInvokeEvent.target.id}`,
        );
      }
      anchorX = enemy.Position.x;
      anchorY = enemy.Position.y;
    }

    // If result_hp is defined, this means the tile hit an enemy in the server simulation
    // So this means the ANCHOR is actually the enemy's position
    // Finally, this means we dont need to add the relative position offsets
    const relativeX = anchoredInvokeEvent.relative_x;
    const relativeY = anchoredInvokeEvent.relative_y;

    // Convert grid tile units to pixel coordinates
    const invokePosition = {
      x: anchorX + relativeX * gridTileSize,
      y: anchorY + relativeY * gridTileSize,
    };

    const skillCastTile = new ClientSkillCastTile({
      position: { x: invokePosition.x, y: invokePosition.y },
      enemies: this.pokemon.Enemies,
      allies: [this.pokemon, ...this.pokemon.Allies] as ClientPokemon[],
      type: skillData.pokemon_type,
      app: this.app!,
      spriteUrl:
        anchoredInvokeEvent.sprite_path ??
        "/assets/spells/impact/tiny-white-1.png",
      parent: this.parent!,
      statusEffects: anchoredInvokeEvent.status_effects,
      sourceId: this.pokemon.id,
      animSpeed: anchoredInvokeEvent.anim_speed ?? 1,
      animLoops: anchoredInvokeEvent.anim_loops ?? 1,
      attackerEffectiveAP: this.pokemon.effectiveAbilityPower,
      healing: anchoredInvokeEvent.healing ?? 0,
      damage: anchoredInvokeEvent.damage ?? 0,
    });

    const spritePath =
      anchoredInvokeEvent.sprite_path ??
      "/assets/spells/impact/tiny-white-1.png";

    skillCastTile
      .initSprite(
        spritePath,
        anchoredInvokeEvent.sprite_scale,
        anchoredInvokeEvent.sprite_scale_x,
        anchoredInvokeEvent.sprite_scale_y,
      )
      .then(() => {
        skillCastTile.fire(
          anchoredInvokeEvent.starting_tick,
          anchoredInvokeEvent.result_hp,
          anchoredInvokeEvent.target.id,
        );
      });
  }

  protected override handleVfx({
    event: vfxEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_Vfx;
    skillData: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;
    let targetPokemon: ClientPokemon | null = null;

    if (!this.app) throw new Error("App not found");

    let position: Position = { x: 0, y: 0 };
    if (vfxEvent.target.type === "self") {
      targetPokemon = this.pokemon;
      position = this.pokemon.Position;
    } else if (vfxEvent.target.type === "ally") {
      const ally = this.pokemon.getAllyPokemon(vfxEvent.target.id);
      if (!ally) {
        console.warn(`Ally not found => id: ${vfxEvent.target.id}`);
        return;
      }

      targetPokemon = ally as ClientPokemon;
      position = ally.Position;
    } else if (vfxEvent.target.type === "enemy") {
      const enemy = this.pokemon.getEnemyPokemon(this.pokemon.TargetId!);
      if (!enemy) {
        console.warn(`Enemy not found => id: ${this.pokemon.TargetId}`);
        return;
      }

      targetPokemon = enemy as ClientPokemon;

      position = enemy.Position;
    }

    // Handle damage if specified
    if (vfxEvent.damage && vfxEvent.damage > 0 && targetPokemon) {
      const damage = calculateDamage({
        inbound: {
          damage: vfxEvent.damage,
          type: skillData.pokemon_type,
          attackerStatusEffects: Array.from(
            this.pokemon.ActiveStatusEffects.values(),
          ),
          attackerEffectiveAP: this.pokemon.effectiveAbilityPower,
        },
        defender: {
          pokemon: targetPokemon.PokemonData,
          defenderStatusEffects: Array.from(
            targetPokemon.ActiveStatusEffects.values(),
          ),
          defenderDodgeChance: targetPokemon.dodgeChance,
        },
      });

      // Show visual damage effect
      targetPokemon.takeDamage(damage);
    }

    // Apply self-damage (recoil) if specified
    if (vfxEvent.self_damage && vfxEvent.self_damage > 0) {
      this.pokemon.takeDamage(vfxEvent.self_damage);
      this.pokemon.showFloatingDamage(vfxEvent.self_damage);
    }

    // Apply status effects from the VFX event
    if (
      vfxEvent.status_effects &&
      vfxEvent.status_effects.length > 0 &&
      targetPokemon
    ) {
      for (const statusEffect of vfxEvent.status_effects) {
        targetPokemon.applyStatusEffect(
          statusEffect,
          this.pokemon.id,
          currentTick + vfxEvent.duration_ticks,
        );
      }
      // Update visual display immediately after applying status effects
      targetPokemon.updateStatusEffectIcons();
    }

    // Apply self status effects if specified
    if (
      vfxEvent.self_status_effects &&
      vfxEvent.self_status_effects.length > 0
    ) {
      for (const statusEffect of vfxEvent.self_status_effects) {
        this.pokemon.applyStatusEffect(
          statusEffect,
          this.pokemon.id,
          currentTick + vfxEvent.duration_ticks,
        );
      }
      // Update visual display for self status effects
      this.pokemon.updateStatusEffectIcons();
    }

    (async () => {
      const vfx = new VFX({
        app: this.app,
        parent: this.parent!,
        spriteUrl: vfxEvent.sprite_path,
        spriteFrameCount:
          VFX_SPRITE_FRAME_COUNT_DATA[vfxEvent.sprite_path] ?? 1,
        spriteScale: vfxEvent.sprite_scale ?? 1,
        spriteScaleX: vfxEvent.sprite_scale_x ?? 1,
        spriteScaleY: vfxEvent.sprite_scale_y ?? 1,
        spriteAlpha: vfxEvent.sprite_alpha ?? 1,
        position: {
          x: position.x,
          y: position.y,
        },
        loops: vfxEvent.loops ?? 1,
        speed: vfxEvent.speed ?? 1,
        ping_pong: vfxEvent.ping_pong ?? false,
        anchorTarget: vfxEvent.anchor_to_target ? targetPokemon : null,
        y_offset: vfxEvent.y_offset ?? 0,
        falling: vfxEvent.falling ?? false,
        duration_ticks: vfxEvent.duration_ticks ?? 0,
      });

      await vfx.load();
      void vfx.play();
    })();
  }

  protected override handleParticleEmission({
    event: particleEmissionEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_ParticleEmission;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    const anchorX = this.pokemon.Position.x;
    const anchorY = this.pokemon.Position.y;

    const direction = this.pokemon.direction as Direction;

    const particleEmissionAreaPattern =
      particleEmissionEvent.should_align_pattern_to_user_dir
        ? particleEmissionEvent.pattern[direction]
        : particleEmissionEvent.pattern.East;

    // Find edge tiles using perimeter detection
    const edgeTiles = this.getPatternEdgeTiles(particleEmissionAreaPattern);

    if (edgeTiles.length === 0) {
      console.warn("No edge tiles found in pattern");
      return;
    }

    // Get frame count for animated sprites
    const spriteFrameCount =
      VFX_SPRITE_FRAME_COUNT_DATA[particleEmissionEvent.sprite_path] ?? 1;

    // Spawn particles with optional staggered timing
    const maxSpawnDelay = particleEmissionEvent.max_spawn_delay_ms ?? 0;

    for (let i = 0; i < particleEmissionEvent.particle_count; i++) {
      // Calculate spawn delay for this particle
      const spawnDelay = maxSpawnDelay > 0 ? Math.random() * maxSpawnDelay : 0;

      setTimeout(() => {
        // Particle spawn position is always the anchor position
        const particleSpawnPosition = {
          x: anchorX,
          y: anchorY,
        };

        // Pick a random edge tile as destination
        const randomEdgeTile =
          edgeTiles[Math.floor(Math.random() * edgeTiles.length)];

        if (!randomEdgeTile) return;

        // Convert grid coordinates to pixel coordinates
        const edgeX = anchorX + randomEdgeTile[0] * gridTileSize;
        const edgeY = anchorY + randomEdgeTile[1] * gridTileSize;

        // Calculate direction vector from anchor to edge
        const dx = edgeX - anchorX;
        const dy = edgeY - anchorY;

        // Extend destination by 25% beyond the edge for better visual effect
        const extensionFactor = 1.25;
        const particleDestination = {
          x: anchorX + dx * extensionFactor,
          y: anchorY + dy * extensionFactor,
        };

        // Create the particle
        new Particle({
          app: this.app,
          parent: this.parent,
          spriteUrl: particleEmissionEvent.sprite_path,
          spriteFrameCount: spriteFrameCount,
          spriteScale: particleEmissionEvent.sprite_scale ?? 1,
          from: particleSpawnPosition,
          to: particleDestination,
          speed: particleEmissionEvent.particle_speed,
          shouldAlignToDestination:
            particleEmissionEvent.should_align_to_destination ?? false,
        });
      }, spawnDelay);
    }
  }

  protected override handleGlobal({
    event: globalEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_Global;
    skillData: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    const vfxConfig = globalEvent.vfx;
    const successfulTargetIds = skillData.successful_target_ids;

    if (!successfulTargetIds)
      throw new Error("Successful target ids not found");

    let anchorTarget: ClientPokemon | null = null;
    if (vfxConfig?.anchor_to_target) {
      if (globalEvent.target.type === "self") {
        anchorTarget = this.pokemon;
      } else if (globalEvent.target.type === "ally") {
        anchorTarget = this.pokemon.getAllyPokemon(
          globalEvent.target.id,
        ) as ClientPokemon | null;
      } else if (globalEvent.target.type === "enemy") {
        anchorTarget = this.pokemon.getEnemyPokemon(
          globalEvent.target.id,
        ) as ClientPokemon | null;
      }
    }

    const delayTicks = globalEvent.effect_delay_ticks;

    // Handle all enemies
    if (globalEvent.target.type === "enemy") {
      const rawDamage = globalEvent.damage;
      for (const enemy of this.pokemon.Enemies) {
        if (enemy.HP.current <= 0) continue;
        if (!successfulTargetIds.includes(enemy.id)) continue;

        const processEnemy = (vfxConfig: SkillEvent_Global["vfx"]) => {
          const damage = calculateDamage({
            inbound: {
              damage: rawDamage ?? 0,
              type: skillData?.pokemon_type,
              attackerStatusEffects: Array.from(
                this.pokemon.ActiveStatusEffects.values(),
              ),
              attackerEffectiveAP: this.pokemon.effectiveAbilityPower,
            },
            defender: {
              pokemon: enemy.PokemonData,
              defenderStatusEffects: Array.from(
                enemy.ActiveStatusEffects.values(),
              ),
              defenderDodgeChance: enemy.dodgeChance,
            },
          });

          if (damage) {
            enemy.takeDamage(damage);
          }

          for (const status of globalEvent.status_effects) {
            enemy.applyStatusEffect(status, this.pokemon.id, currentTick);
          }

          if (!vfxConfig) return;

          (async () => {
            const vfx = new VFX({
              app: this.app,
              parent: this.parent!,
              spriteUrl: vfxConfig.sprite_path,
              spriteFrameCount:
                VFX_SPRITE_FRAME_COUNT_DATA[vfxConfig.sprite_path] ?? 1,
              spriteScale: vfxConfig.sprite_scale ?? 1,
              spriteScaleX: vfxConfig.sprite_scale_x ?? 1,
              spriteScaleY: vfxConfig.sprite_scale_y ?? 1,
              spriteAlpha: vfxConfig.sprite_alpha ?? 1,
              position: {
                x: enemy.Position.x,
                y: enemy.Position.y,
              },
              loops: vfxConfig.loops ?? 1,
              speed: vfxConfig.speed ?? 1,
              ping_pong: false,
              anchorTarget: enemy,
              y_offset: vfxConfig.y_offset ?? 0,
              falling: vfxConfig.falling ?? false,
              duration_ticks: globalEvent.duration_ticks ?? 0,
            });

            await vfx.load();
            void vfx.play();
          })();
        };

        if (delayTicks) {
          setTimeout(() => {
            processEnemy(vfxConfig);
          }, delayTicks * MS_PER_TICK);
        } else {
          processEnemy(vfxConfig);
        }
      }
      return;
    }

    // Handle all allies
    const healing = globalEvent.healing;

    for (const ally of this.pokemon.Allies) {
      if (ally.HP.current <= 0) continue;
      if (!successfulTargetIds.includes(ally.id)) continue;

      const processAlly = (vfxConfig: SkillEvent_Global["vfx"]) => {
        if (healing) {
          const healMultiplier = getHealingMultiplier(
            this.pokemon.effectiveAbilityPower,
          );
          const healAmount = Math.round(healing * healMultiplier);
          ally.heal(healAmount);
        }

        for (const status of globalEvent.status_effects) {
          ally.applyStatusEffect(status, this.pokemon.id, currentTick);
        }

        if (!vfxConfig) return;

        (async () => {
          const vfx = new VFX({
            app: this.app,
            parent: this.parent!,
            spriteUrl: vfxConfig.sprite_path,
            spriteFrameCount:
              VFX_SPRITE_FRAME_COUNT_DATA[vfxConfig.sprite_path] ?? 1,
            spriteScale: vfxConfig.sprite_scale ?? 1,
            spriteScaleX: vfxConfig.sprite_scale_x ?? 1,
            spriteScaleY: vfxConfig.sprite_scale_y ?? 1,
            spriteAlpha: vfxConfig.sprite_alpha ?? 1,
            position: {
              x: ally.Position.x,
              y: ally.Position.y,
            },
            loops: vfxConfig.loops ?? 1,
            speed: vfxConfig.speed ?? 1,
            ping_pong: false,
            anchorTarget: ally as ClientPokemon | null,
            y_offset: vfxConfig.y_offset ?? 0,
            falling: vfxConfig.falling ?? false,
            duration_ticks: globalEvent.duration_ticks ?? 0,
          });

          await vfx.load();
          void vfx.play();
        })();
      };

      if (delayTicks) {
        setTimeout(() => {
          processAlly(vfxConfig);
        }, delayTicks * MS_PER_TICK);
      } else {
        processAlly(vfxConfig);
      }
    }
  }

  protected handleStealBuff({
    event: stealBuffEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_StealBuff;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    const currentTick = _currentTick ?? this.currentTick;

    // Update status icons for the target who lost buffs
    const targetPokemon = this.pokemon.getEnemyPokemon(
      stealBuffEvent.target.id,
    ) as ClientPokemon | null;
    if (targetPokemon) {
      targetPokemon.updateStatusEffectIcons();
    }

    // Apply stolen buffs to caster
    if (stealBuffEvent.stolen_buffs && stealBuffEvent.stolen_buffs.length > 0) {
      for (const buff of stealBuffEvent.stolen_buffs) {
        this.pokemon.applyStatusEffect(
          buff,
          targetPokemon?.id ?? 0,
          currentTick,
        );
      }
    }

    // Update status icons for the caster who gained buffs
    this.pokemon.updateStatusEffectIcons();
  }

  protected handleCleanse({
    event: cleanseEvent,
    skillData,
    currentTick: _currentTick,
  }: {
    event: SkillEvent_Cleanse;
    skillData?: SkillData;
    currentTick?: number;
  }) {
    // Update status icons for the target who had debuffs removed
    let targetPokemon: ClientPokemon | null = null;

    if (cleanseEvent.target.type === "self") {
      targetPokemon = this.pokemon;
    } else if (cleanseEvent.target.type === "ally") {
      targetPokemon = this.pokemon.getAllyPokemon(
        cleanseEvent.target.id,
      ) as ClientPokemon | null;
    } else if (cleanseEvent.target.type === "enemy") {
      targetPokemon = this.pokemon.getEnemyPokemon(
        cleanseEvent.target.id,
      ) as ClientPokemon | null;
    }

    if (targetPokemon) {
      targetPokemon.updateStatusEffectIcons();
    }
  }

  /**
   * Find edge tiles in a pattern using perimeter detection
   * A tile is an edge if it has any adjacent position that's NOT in the pattern
   */
  private getPatternEdgeTiles(
    pattern: Array<[number, number, boolean?]>,
  ): Array<[number, number]> {
    // Create a set for fast lookup
    const patternSet = new Set(pattern.map(([x, y]) => `${x},${y}`));

    const edgeTiles: Array<[number, number]> = [];

    for (const [x, y] of pattern) {
      // Check 8 adjacent positions
      const neighbors = [
        [x - 1, y],
        [x + 1, y],
        [x, y - 1],
        [x, y + 1],
        [x - 1, y - 1],
        [x + 1, y - 1],
        [x - 1, y + 1],
        [x + 1, y + 1],
      ];

      // If any neighbor is NOT in pattern, this is an edge tile
      const isEdge = neighbors.some(
        ([nx, ny]) => !patternSet.has(`${nx},${ny}`),
      );

      if (isEdge) {
        edgeTiles.push([x, y]);
      }
    }

    return edgeTiles;
  }

  /**
   * Handle CooldownSkip event - visual feedback only (logic is server-side)
   */
  protected handleCooldownSkip({
    event: cooldownSkipEvent,
    skillData,
  }: {
    event: SkillEvent_CooldownSkip;
    skillData?: SkillData;
  }) {
    // Play a VFX on the targets to indicate cooldown skip
    const targets: ClientPokemon[] = [];

    switch (cooldownSkipEvent.target_scope) {
      case "self":
        targets.push(this.pokemon);
        break;
      case "ally":
        if (this.pokemon.Allies[0]) {
          targets.push(this.pokemon.Allies[0] as ClientPokemon);
        }
        break;
      case "all_allies":
        targets.push(this.pokemon);
        for (const ally of this.pokemon.Allies) {
          targets.push(ally as ClientPokemon);
        }
        break;
    }

    // Play a speed-up VFX on each target
    for (const target of targets) {
      (async () => {
        const vfx = new VFX({
          app: this.app,
          parent: this.parent,
          spriteUrl: "/assets/spells/vfx/stars_effect_001__40.png",
          spriteFrameCount:
            VFX_SPRITE_FRAME_COUNT_DATA[
              "/assets/spells/vfx/stars_effect_001__40.png"
            ] ?? 1,
          spriteScale: 1.5,
          spriteScaleX: 1,
          spriteScaleY: 1,
          spriteAlpha: 1,
          position: target.Position,
          loops: 1,
          speed: 0.8,
          ping_pong: false,
          anchorTarget: target,
          y_offset: -30,
          falling: false,
          duration_ticks: cooldownSkipEvent.duration_ticks,
        });
        await vfx.load();
        void vfx.play();
      })();
    }
  }

  /**
   * Handle Tether event - create a visual link between two pokemon
   */
  protected handleTether({
    event: tetherEvent,
    skillData,
  }: {
    event: SkillEvent_Tether;
    skillData?: SkillData;
  }) {
    if (!tetherEvent.battle_log?.tether_target_id) {
      console.warn("Tether event missing target ID");
      return;
    }

    const targetId = tetherEvent.battle_log.tether_target_id;
    let tetherTarget: ClientPokemon | null = null;

    if (tetherEvent.tether_type === "ally") {
      tetherTarget = this.pokemon.getAllyPokemon(targetId) as ClientPokemon;
    } else {
      tetherTarget = this.pokemon.getEnemyPokemon(targetId) as ClientPokemon;
    }

    if (!tetherTarget) {
      console.warn("Tether target not found:", targetId);
      return;
    }

    // Extract config with defaults
    const config = tetherEvent.tether_line_config;
    const shouldDraw = config?.enabled !== false; // Default: true

    if (!shouldDraw) return;

    const defaultColor =
      tetherEvent.tether_type === "ally" ? 0x33ff33 : 0xff3333;
    const tetherColor = config?.color ?? defaultColor;
    const tetherWidth = config?.width ?? 3;
    const hasGlow = config?.glow !== false; // Default: true
    const hasPulse = config?.pulse !== false; // Default: true
    const hasEnergyFlow = config?.energy_flow === true; // Default: false

    // Create tether visual using Graphics line
    const tetherLine = new Graphics();
    tetherLine.zIndex = 10;
    this.parent.addChild(tetherLine);

    // Create glow layer (rendered behind main line)
    let glowLine: Graphics | null = null;
    if (hasGlow) {
      glowLine = new Graphics();
      glowLine.zIndex = 11;
      this.parent.addChild(glowLine);
    }

    // Energy particles for flow effect
    const energyParticles: Array<{ t: number; speed: number }> = [];
    if (hasEnergyFlow) {
      // Initialize 4-6 particles at random positions along the line
      for (let i = 0; i < 15; i++) {
        energyParticles.push({
          t: Math.random(),
          speed: 0.63 + Math.random() * 0.84,
        });
      }
    }

    const durationMs = tetherEvent.duration_ticks * MS_PER_TICK;
    const startTime = performance.now();
    const maxDistancePixels = tetherEvent.max_distance * gridTileSize;

    // Track periodic effects timing
    const effectIntervalMs = tetherEvent.tick_interval * MS_PER_TICK;
    const casterEffects = tetherEvent.caster_effects;
    const targetEffects = tetherEvent.target_effects;
    // Legacy fallback
    const legacyTargetHeal = tetherEvent.periodic_heal;
    let lastEffectTime = startTime;

    const updateTether = () => {
      const elapsed = performance.now() - startTime;
      if (elapsed >= durationMs) {
        tetherLine.destroy();
        if (glowLine) glowLine.destroy();
        return;
      }

      const casterX = this.pokemon.Position.x;
      const casterY = this.pokemon.Position.y;
      const targetX = tetherTarget!.Position.x;
      const targetY = tetherTarget!.Position.y;

      // Break tether if either Pokemon dies
      if (this.pokemon.HP.current <= 0 || tetherTarget!.HP.current <= 0) {
        tetherLine.destroy();
        if (glowLine) glowLine.destroy();
        return;
      }

      // Check if tether should break due to distance (Chebyshev distance in pixels)
      const dx = Math.abs(casterX - targetX);
      const dy = Math.abs(casterY - targetY);
      const distancePixels = Math.max(dx, dy);
      if (distancePixels > maxDistancePixels) {
        tetherLine.destroy();
        if (glowLine) glowLine.destroy();
        return;
      }

      // Show periodic effects
      const now = performance.now();
      if (now - lastEffectTime >= effectIntervalMs) {
        // Caster effects
        if (casterEffects?.periodicHeal) {
          (this.pokemon as ClientPokemon).showFloatingHeal(
            casterEffects.periodicHeal,
          );
        }
        if (casterEffects?.periodicDamage) {
          (this.pokemon as ClientPokemon).showFloatingDamage(
            casterEffects.periodicDamage,
          );
        }
        if (casterEffects?.periodicStatus) {
          (this.pokemon as ClientPokemon).applyStatusEffect(
            casterEffects.periodicStatus,
          );
        }
        // Target effects
        if (targetEffects?.periodicHeal) {
          tetherTarget!.showFloatingHeal(targetEffects.periodicHeal);
        }
        if (targetEffects?.periodicDamage) {
          tetherTarget!.showFloatingDamage(targetEffects.periodicDamage);
        }
        if (targetEffects?.periodicStatus) {
          tetherTarget!.applyStatusEffect(targetEffects.periodicStatus);
        }
        // Legacy fallback
        if (!casterEffects && !targetEffects && legacyTargetHeal) {
          tetherTarget!.showFloatingHeal(legacyTargetHeal);
        }

        //
        lastEffectTime = now;
      }

      // Pulse effect: oscillating alpha and width
      let pulseAlpha = 0.5;
      let pulseWidth = tetherWidth;
      if (hasPulse) {
        const pulseT = Math.sin(elapsed * 0.008) * 0.5 + 0.5; // 0-1 oscillation
        pulseAlpha = 0.3 + pulseT * 0.3; // 0.6-0.95
        pulseWidth = tetherWidth * (0.8 + pulseT * 0.5); // 80%-120% of base width
      }

      // Clear and redraw glow layer
      if (glowLine) {
        glowLine.clear();
        // Draw multiple layers for glow effect
        const glowLayers = [
          { width: pulseWidth * 4, alpha: 0.1 },
          { width: pulseWidth * 2.5, alpha: 0.2 },
          { width: pulseWidth * 1.5, alpha: 0.3 },
        ];
        for (const layer of glowLayers) {
          glowLine.moveTo(casterX, casterY);
          glowLine.lineTo(targetX, targetY);
          glowLine.stroke({
            width: layer.width,
            color: tetherColor,
            alpha: layer.alpha * pulseAlpha,
          });
        }
      }

      // Clear and redraw main line
      tetherLine.clear();
      tetherLine.moveTo(casterX, casterY);
      tetherLine.lineTo(targetX, targetY);
      tetherLine.stroke({
        width: pulseWidth,
        color: tetherColor,
        alpha: pulseAlpha,
      });

      // Draw energy flow particles
      if (hasEnergyFlow && energyParticles.length > 0) {
        const dx = targetX - casterX;
        const dy = targetY - casterY;

        for (const particle of energyParticles) {
          // Update particle position along line
          particle.t += particle.speed * 0.016; // ~60fps
          if (particle.t > 1) particle.t = 0; // Loop back

          const px = casterX + dx * particle.t;
          const py = casterY + dy * particle.t;

          // Draw particle as small circle with tether color (brighter core)
          tetherLine.circle(px, py, pulseWidth * 1.25);
          tetherLine.fill({ color: tetherColor, alpha: 0.15 });
        }
      }

      requestAnimationFrame(updateTether);
    };

    requestAnimationFrame(updateTether);
  }

  /**
   * Handle Pull event - animate targets being pulled toward caster
   */
  protected handlePull({
    event: pullEvent,
    skillData,
  }: {
    event: SkillEvent_Pull;
    skillData?: SkillData;
  }) {
    if (!pullEvent.battle_log?.pulled_targets) {
      console.warn("Pull event missing pulled_targets");
      return;
    }

    const durationMs = pullEvent.duration_ticks * MS_PER_TICK;
    const casterPos = this.pokemon.Position;

    for (const pullData of pullEvent.battle_log.pulled_targets) {
      const target = this.pokemon.getEnemyPokemon(pullData.id) as ClientPokemon;
      if (!target) continue;

      // Convert grid positions to pixel positions (including grid margins)
      const fromPixel = {
        x: (pullData.from.x + 1) * gridTileSize + gridMarginX,
        y: (pullData.from.y + 1) * gridTileSize + gridMarginY,
      };
      const toPixel = {
        x: (pullData.to.x + 1) * gridTileSize + gridMarginX,
        y: (pullData.to.y + 1) * gridTileSize + gridMarginY,
      };

      // Create hook line graphic if enabled
      const hookLineConfig = pullEvent.hook_line_config;
      const shouldDrawHook = hookLineConfig?.enabled !== false; // Default: true
      const hookColor = hookLineConfig?.color ?? 0xff6b35; // Default: orange-red
      const hookWidth = hookLineConfig?.width ?? 6; // Default: 6 pixels

      let hookLine: Graphics | null = null;
      if (shouldDrawHook) {
        hookLine = new Graphics();
        hookLine.zIndex = 500;
        this.parent.addChild(hookLine);
      }

      // Animate the hook line and target
      const startTime = performance.now();

      const animatePull = () => {
        const elapsed = performance.now() - startTime;
        const t = Math.min(1, elapsed / durationMs);

        // Phase timing:
        // 0-15%: Hook grows fast to target's ORIGINAL position
        // 15-30%: Tension - hook and target stay in place
        // 30-100%: Target gets pulled to caster, hook follows

        // Calculate hook line extension (0->1 in first 15%, stays 1 after)
        const hookExtendT = Math.min(1, t / 0.15);

        // Calculate target pull progress (0 until 30%, then 0->1 from 30-100%)
        let pullT = 0;
        if (t > 0.3) {
          pullT = (t - 0.3) / 0.7; // 0->1 over remaining 70%
        }
        const pullEase = 1 - Math.pow(1 - pullT, 3); // Ease out

        // Update target position - only moves after tension phase
        target.Position.x = fromPixel.x + (toPixel.x - fromPixel.x) * pullEase;
        target.Position.y = fromPixel.y + (toPixel.y - fromPixel.y) * pullEase;

        // Draw hook line if enabled
        if (hookLine) {
          hookLine.clear();

          // Hook line: from caster to target's CURRENT position
          // During grow phase (0-15%): grows toward original position
          // After grow: follows target as it's pulled
          const casterX = this.pokemon.Position.x;
          const casterY = this.pokemon.Position.y;

          // Line endpoint: interpolate from caster toward target's current pos
          const lineEndX =
            casterX + (target.Position.x - casterX) * hookExtendT;
          const lineEndY =
            casterY + (target.Position.y - casterY) * hookExtendT;

          hookLine.moveTo(casterX, casterY);
          hookLine.lineTo(lineEndX, lineEndY);
          hookLine.stroke({
            width: hookWidth,
            color: hookColor,
            alpha: 0.9,
          });
        }

        if (t < 1) {
          requestAnimationFrame(animatePull);
        } else {
          // Apply stun if specified
          if (pullEvent.stun_ticks && pullEvent.stun_ticks > 0) {
            target.applyStatusEffect(
              {
                status: StatusEffect.STUN,
                duration_ticks: pullEvent.stun_ticks,
                intensity: 1,
              },
              this.pokemon.id,
              pullEvent.starting_tick,
            );
            target.updateStatusEffectIcons();
          }
          if (hookLine) {
            hookLine.destroy();
          }
        }
      };

      requestAnimationFrame(animatePull);

      // Play pull VFX if specified
      if (pullEvent.pull_vfx) {
        (async () => {
          const vfx = new VFX({
            app: this.app,
            parent: this.parent,
            spriteUrl: pullEvent.pull_vfx!.sprite_path,
            spriteFrameCount:
              VFX_SPRITE_FRAME_COUNT_DATA[pullEvent.pull_vfx!.sprite_path] ?? 1,
            spriteScale: 1,
            spriteScaleX: 1,
            spriteScaleY: 1,
            spriteAlpha: 1,
            position: target.Position,
            loops: 1,
            speed: pullEvent.pull_vfx!.speed,
            ping_pong: false,
            anchorTarget: target,
            y_offset: 0,
            falling: false,
            duration_ticks: pullEvent.duration_ticks,
          });
          await vfx.load();
          void vfx.play();
        })();
      }
    }
  }
}
